// SNIGJ - Enhanced Admin Page

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { adminAPI, referenceAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Shield, 
  Search, 
  Users,
  Edit,
  UserCircle,
  Calendar,
  Mail,
  Plus,
  Send,
  DollarSign,
  Bell,
  FileText,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminPage = () => {
  const { language, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  
  const [editData, setEditData] = useState({ 
    role: '', 
    clearance_level: '',
    province: '',
    territorial_level: ''
  });

  const [feeData, setFeeData] = useState({
    citizen_id: '',
    fee_type: 'COURT_FEE',
    amount: '',
    currency: 'USD',
    description: '',
    due_date: ''
  });

  const [notificationData, setNotificationData] = useState({
    recipient_id: '',
    notification_type: 'SUMMONS',
    title: '',
    message: '',
    scheduled_date: '',
    location: '',
    is_urgent: false
  });

  const roles = [
    'CITIZEN', 'POLICE_OFFICER', 'JUDICIAL_POLICE_OFFICER', 'CLERK', 'MAGISTRATE',
    'PRISON_OFFICER', 'PRISON_DIRECTOR', 'BORDER_CONTROL_OFFICER',
    'PROVINCIAL_ADMIN', 'NATIONAL_ADMIN', 'TECH_SUPPORT',
    'FINANCIAL_AUDITOR', 'MINISTER', 'PRESIDENT', 'SUPER_ADMIN'
  ];

  const classifications = ['PUBLIC', 'RESTRICTED', 'CONFIDENTIAL', 'SECRET', 'TOP_SECRET'];
  const territorialLevels = ['SUB_COMMISSION', 'COMMISSION', 'TERRITORY', 'CITY', 'PROVINCE', 'NATIONAL'];
  const feeTypes = ['COURT_FEE', 'FINE', 'PENALTY', 'ADMINISTRATIVE_FEE'];
  const notificationTypes = ['SUMMONS', 'INVITATION', 'REMINDER', 'ALERT', 'PAYMENT_DUE'];

  const hasAdminAccess = ['NATIONAL_ADMIN', 'TECH_SUPPORT', 'MINISTER', 'PRESIDENT', 'SUPER_ADMIN'].includes(user?.role);

  useEffect(() => {
    if (hasAdminAccess) {
      loadUsers();
      loadProvinces();
    }
  }, [hasAdminAccess, roleFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = roleFilter !== 'ALL' ? { role: roleFilter } : {};
      const response = await adminAPI.listUsers(params);
      // Handle both array and object response formats
      const userData = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setUsers(userData);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadProvinces = async () => {
    try {
      const response = await api.get('/reference/provinces');
      setProvinces(response.data.provinces || []);
    } catch (error) {
      console.error('Failed to load provinces');
    }
  };

  const handleEditUser = (userItem) => {
    setSelectedUser(userItem);
    setEditData({
      role: userItem.role,
      clearance_level: userItem.clearance_level,
      province: userItem.province || '',
      territorial_level: userItem.territorial_level || 'NATIONAL'
    });
    setShowEditModal(true);
  };

  const handleUpdateRole = async () => {
    try {
      await adminAPI.updateUserRole(selectedUser.user_id, editData);
      toast.success(language === 'fr' ? 'Utilisateur mis à jour' : 'User updated');
      setShowEditModal(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de mise à jour' : 'Update failed'));
    }
  };

  const handleCreateFee = async () => {
    try {
      await api.post('/admin/judicial-fees', feeData);
      toast.success(language === 'fr' ? 'Frais créé' : 'Fee created');
      setShowFeeModal(false);
      setFeeData({
        citizen_id: '',
        fee_type: 'COURT_FEE',
        amount: '',
        currency: 'USD',
        description: '',
        due_date: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation failed'));
    }
  };

  const handleCreateNotification = async () => {
    try {
      await api.post('/admin/notifications', notificationData);
      toast.success(language === 'fr' ? 'Notification envoyée' : 'Notification sent');
      setShowNotificationModal(false);
      setNotificationData({
        recipient_id: '',
        notification_type: 'SUMMONS',
        title: '',
        message: '',
        scheduled_date: '',
        location: '',
        is_urgent: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur d\'envoi' : 'Send failed'));
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!hasAdminAccess) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="font-heading text-xl font-bold mb-2">
                {language === 'fr' ? 'Accès Restreint' : 'Access Restricted'}
              </h2>
              <p className="text-muted-foreground">
                {language === 'fr' 
                  ? "Vous n'avez pas les droits d'accès à ce module."
                  : "You don't have access to this module."}
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {t(language, 'admin.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Gestion des utilisateurs, rôles et notifications' : 'User, role and notification management'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFeeModal(true)} className="gap-2">
              <DollarSign className="w-4 h-4" />
              {language === 'fr' ? 'Créer frais' : 'Create Fee'}
            </Button>
            <Button onClick={() => setShowNotificationModal(true)} className="gap-2">
              <Send className="w-4 h-4" />
              {language === 'fr' ? 'Envoyer convocation' : 'Send Summons'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{users.length}</p>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Utilisateurs totaux' : 'Total Users'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <UserCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {users.filter(u => u.role === 'CITIZEN').length}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Citoyens' : 'Citizens'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {users.filter(u => u.role !== 'CITIZEN').length}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Agents' : 'Officials'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Shield className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">
                  {users.filter(u => ['SECRET', 'TOP_SECRET'].includes(u.clearance_level)).length}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Habilitations élevées' : 'High Clearance'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t(language, 'common.search') + '...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="user-search"
                  />
                </div>
              </div>
              <div className="w-[200px]">
                <Select
                  value={roleFilter}
                  onValueChange={setRoleFilter}
                >
                  <SelectTrigger data-testid="role-filter">
                    <SelectValue placeholder={language === 'fr' ? 'Filtrer par rôle' : 'Filter by role'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous les rôles' : 'All roles'}</SelectItem>
                    {roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {t(language, `roles.${role}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              {t(language, 'admin.users')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="bg-muted/50">
                    <th>{language === 'fr' ? 'Utilisateur' : 'User'}</th>
                    <th>{t(language, 'auth.email')}</th>
                    <th>{t(language, 'admin.role')}</th>
                    <th>{t(language, 'admin.clearanceLevel')}</th>
                    <th>{language === 'fr' ? 'Province' : 'Province'}</th>
                    <th>{language === 'fr' ? 'Niveau' : 'Level'}</th>
                    <th>{t(language, 'cases.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j}><div className="h-4 skeleton" /></td>
                        ))}
                      </tr>
                    ))
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground">
                        {t(language, 'common.noData')}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((userItem) => (
                      <tr key={userItem.user_id} data-testid={`user-row-${userItem.user_id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            {userItem.picture ? (
                              <img src={userItem.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <UserCircle className="w-8 h-8 text-muted-foreground" />
                            )}
                            <span className="font-medium">{userItem.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="text-sm">{userItem.email}</span>
                        </td>
                        <td>
                          <span className={`px-2 py-1 rounded text-xs ${
                            userItem.role === 'CITIZEN' ? 'bg-blue-100 text-blue-800' :
                            userItem.role === 'SUPER_ADMIN' ? 'bg-red-100 text-red-800' :
                            'bg-slate-100 dark:bg-slate-800'
                          }`}>
                            {t(language, `roles.${userItem.role}`)}
                          </span>
                        </td>
                        <td>
                          <span className={`px-2 py-1 rounded text-xs classification-${userItem.clearance_level?.toLowerCase()}`}>
                            {t(language, `classifications.${userItem.clearance_level}`)}
                          </span>
                        </td>
                        <td>
                          <span className="text-sm">{userItem.province || '-'}</span>
                        </td>
                        <td>
                          <span className="text-sm">{userItem.territorial_level || '-'}</span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleEditUser(userItem)}
                              data-testid={`edit-user-${userItem.user_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {userItem.role === 'CITIZEN' && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => {
                                    setFeeData({ ...feeData, citizen_id: userItem.user_id });
                                    setShowFeeModal(true);
                                  }}
                                  title={language === 'fr' ? 'Créer un frais' : 'Create fee'}
                                >
                                  <DollarSign className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => {
                                    setNotificationData({ ...notificationData, recipient_id: userItem.user_id });
                                    setShowNotificationModal(true);
                                  }}
                                  title={language === 'fr' ? 'Envoyer notification' : 'Send notification'}
                                >
                                  <Bell className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Edit User Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {language === 'fr' ? 'Modifier les privilèges' : 'Edit Privileges'} - {selectedUser?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t(language, 'admin.role')}</Label>
                <Select
                  value={editData.role}
                  onValueChange={(value) => setEditData({ ...editData, role: value })}
                >
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {t(language, `roles.${role}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t(language, 'admin.clearanceLevel')}</Label>
                <Select
                  value={editData.clearance_level}
                  onValueChange={(value) => setEditData({ ...editData, clearance_level: value })}
                >
                  <SelectTrigger data-testid="edit-user-clearance">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {classifications.map((c) => (
                      <SelectItem key={c} value={c}>
                        {t(language, `classifications.${c}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Province' : 'Province'}</Label>
                <Select
                  value={editData.province || "NONE"}
                  onValueChange={(value) => setEditData({ ...editData, province: value === "NONE" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">{language === 'fr' ? 'National (toutes)' : 'National (all)'}</SelectItem>
                    {provinces.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Niveau territorial' : 'Territorial Level'}</Label>
                <Select
                  value={editData.territorial_level}
                  onValueChange={(value) => setEditData({ ...editData, territorial_level: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {territorialLevels.map((level) => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  {t(language, 'common.cancel')}
                </Button>
                <Button onClick={handleUpdateRole} data-testid="update-user-submit">
                  {t(language, 'admin.updateRole')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Fee Modal */}
        <Dialog open={showFeeModal} onOpenChange={setShowFeeModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {language === 'fr' ? 'Créer une note de perception' : 'Create Perception Note'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'ID Citoyen' : 'Citizen ID'}</Label>
                <Input
                  value={feeData.citizen_id}
                  onChange={(e) => setFeeData({ ...feeData, citizen_id: e.target.value })}
                  placeholder="user_xxxx"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Type de frais' : 'Fee Type'}</Label>
                  <Select
                    value={feeData.fee_type}
                    onValueChange={(value) => setFeeData({ ...feeData, fee_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {feeTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Montant' : 'Amount'}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={feeData.amount}
                    onChange={(e) => setFeeData({ ...feeData, amount: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                <Textarea
                  value={feeData.description}
                  onChange={(e) => setFeeData({ ...feeData, description: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Date d\'échéance' : 'Due Date'}</Label>
                <Input
                  type="date"
                  value={feeData.due_date}
                  onChange={(e) => setFeeData({ ...feeData, due_date: e.target.value })}
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowFeeModal(false)}>
                  {t(language, 'common.cancel')}
                </Button>
                <Button onClick={handleCreateFee}>
                  {language === 'fr' ? 'Créer' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Notification Modal */}
        <Dialog open={showNotificationModal} onOpenChange={setShowNotificationModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {language === 'fr' ? 'Envoyer une convocation' : 'Send Summons'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'ID Destinataire' : 'Recipient ID'}</Label>
                <Input
                  value={notificationData.recipient_id}
                  onChange={(e) => setNotificationData({ ...notificationData, recipient_id: e.target.value })}
                  placeholder="user_xxxx"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                <Select
                  value={notificationData.notification_type}
                  onValueChange={(value) => setNotificationData({ ...notificationData, notification_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {notificationTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                <Input
                  value={notificationData.title}
                  onChange={(e) => setNotificationData({ ...notificationData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Message' : 'Message'}</Label>
                <Textarea
                  value={notificationData.message}
                  onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Date de convocation' : 'Scheduled Date'}</Label>
                  <Input
                    type="date"
                    value={notificationData.scheduled_date}
                    onChange={(e) => setNotificationData({ ...notificationData, scheduled_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Lieu' : 'Location'}</Label>
                  <Input
                    value={notificationData.location}
                    onChange={(e) => setNotificationData({ ...notificationData, location: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="urgent"
                  checked={notificationData.is_urgent}
                  onChange={(e) => setNotificationData({ ...notificationData, is_urgent: e.target.checked })}
                />
                <Label htmlFor="urgent">{language === 'fr' ? 'Urgent' : 'Urgent'}</Label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowNotificationModal(false)}>
                  {t(language, 'common.cancel')}
                </Button>
                <Button onClick={handleCreateNotification} className="gap-2">
                  <Send className="w-4 h-4" />
                  {language === 'fr' ? 'Envoyer' : 'Send'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminPage;
